// components
import { SideEditorMenu, SideEditorMenuItem } from '@components/SideEditor/SideEditor';
import { useUser } from '@context/user-context';
import { faCog, faImages, faPalette, faShareAlt, faStream, faUser } from '@fortawesome/pro-light-svg-icons';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTeamRole } from '@hooks/team-workspace';
import { generateEntityId } from '@modules/helpers';
import { type FC, useEffect, useMemo, useState } from 'react';

import styles from './EmailSignatureEditorMenu.module.scss';

const MenuItem: FC<
  React.PropsWithChildren<{
    title: string;
    itemIcon: IconDefinition;
    index: number;
  }>
> = ({ title, itemIcon, index }) => {
  return (
    <SideEditorMenuItem icon={<FontAwesomeIcon icon={itemIcon} size="2x" />} index={index} title={title} />
  );
};

type EmailSignatureSideEditorMenuType = {
  emailSignatureHasBeenLoaded: boolean;
  isTeamEmailSignature: boolean;
  emailSignatureOrgId: string | undefined;
};

export const MenuItemDivider: FC<React.PropsWithChildren<unknown>> = () => {
  return <div className={styles['em-sig-editor-divider']} />;
};

const EmailSignatureSideEditorMenu: FC<React.PropsWithChildren<EmailSignatureSideEditorMenuType>> = ({
  emailSignatureHasBeenLoaded,
  isTeamEmailSignature,
  emailSignatureOrgId,
}) => {
  const userDetails = useUser();
  const { atLeastRole } = useTeamRole();
  const isTeamAdmin = atLeastRole('ADMIN');

  const [sideEditorKey, setSideEditorKey] = useState<string>(generateEntityId());

  const displaySettingTab = useMemo(() => {
    return isTeamAdmin ? userDetails.user?.bbOrgId === emailSignatureOrgId : false;
  }, [emailSignatureOrgId, isTeamAdmin, userDetails.user?.bbOrgId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    // this is a fix for reach ui bug where the indices of the menu item is messed up when
    // you remove or add extra tab
    setSideEditorKey(generateEntityId());
  }, [isTeamEmailSignature, emailSignatureHasBeenLoaded]);

  return (
    <SideEditorMenu key={sideEditorKey}>
      <MenuItem index={0} title={'Details'} itemIcon={faUser} />
      <MenuItem index={1} title={'Images'} itemIcon={faImages} />
      <MenuItem index={2} title={'Links'} itemIcon={faShareAlt} />
      <MenuItem index={3} title={'Design'} itemIcon={faPalette} />
      <MenuItemDivider />
      <MenuItem index={4} title={'Banners'} itemIcon={faStream} />
      {displaySettingTab && isTeamEmailSignature && (
        <>
          <div className={styles['em-sig-editor-divider']}></div>
          <MenuItem index={5} title={'Settings'} itemIcon={faCog} />
        </>
      )}
    </SideEditorMenu>
  );
};

export default EmailSignatureSideEditorMenu;
